import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import https from '../../utils/https';
import { QueryParams, buildQueryString } from '../types';

export interface Category {
    _id?: string;
    name: string;
    slug?: string;
    description: string;
    main_category_id?: string;
    brand_id?: string;
    mainCategory?: { _id: string; name: string };
    brand?: { _id: string; name: string };
    status?: string;
    image: string;
    createdAt?: string;
    updatedAt?: string;
}

interface CategoryState {
    categories: Category[];
    hoveredCategories: Record<string, Category[]>;
    loading: boolean;
    error: string | null;
}

const initialState: CategoryState = {
    categories: [],
    hoveredCategories: {},
    loading: false,
    error: null,
};

export const fetchCategories = createAsyncThunk('category/fetchCategories', async (params: QueryParams | undefined, { rejectWithValue }) => {
    try {
        const mergedParams = { sort: { priority: 1 }, limit: 1000, ...params };
        const queryString = buildQueryString(mergedParams);
        const response = await https.get(`categories${queryString}`);
        return response.data || [];
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to fetch categories');
    }
});

export const fetchCategoriesByMainCategory = createAsyncThunk(
    'category/fetchCategoriesByMainCategory',
    async (mainCategoryId: string, { rejectWithValue }) => {
        try {
            const filter = { "main_category": { "_id": mainCategoryId } };
            const queryString = buildQueryString({ filter, sort: { priority: 1 }, limit: 1000 });
            const response = await https.get(`categories${queryString}`);
            return { mainCategoryId, categories: response.data || [] };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch categories');
        }
    }
);

export const fetchCategoriesSelect = createAsyncThunk('category/fetchCategoriesSelect', async (params: QueryParams | undefined, { rejectWithValue }) => {
    try {
        const queryString = buildQueryString(params || {});
        const response = await https.get(`categories/select${queryString}`);
        return response.data || [];
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to fetch categories');
    }
});

export const fetchCategoryById = createAsyncThunk('category/fetchCategoryById', async (id: string, { rejectWithValue }) => {
    try {
        const response = await https.get(`categories/${id}`);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to fetch category');
    }
});

export const addCategory = createAsyncThunk('category/addCategory', async (category: FormData, { rejectWithValue }) => {
    try {
        const response = await https.post('categories', category);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to add category');
    }
});

export const updateCategory = createAsyncThunk('category/updateCategory', async ({ id, category }: { id: string; category: FormData }, { rejectWithValue }) => {
    try {
        const response = await https.put(`categories/${id}`, category);
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to update category');
    }
});

export const deleteCategory = createAsyncThunk('category/deleteCategory', async (id: string, { rejectWithValue }) => {
    try {
        await https.delete(`categories/${id}`);
        return id;
    } catch (error: any) {
        return rejectWithValue(error.message || 'Failed to delete category');
    }
});

const categorySlice = createSlice({
    name: 'category',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch Categories
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action: PayloadAction<Category[]>) => {
                state.loading = false;
                state.categories = [...action.payload].sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999));
            })
            .addCase(fetchCategoriesSelect.fulfilled, (state, action: PayloadAction<Category[]>) => {
                state.categories = [...action.payload].sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999));
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Fetch Categories By Main Category
            .addCase(fetchCategoriesByMainCategory.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategoriesByMainCategory.fulfilled, (state, action) => {
                state.loading = false;
                state.hoveredCategories[action.payload.mainCategoryId] = [...action.payload.categories].sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999));
            })
            .addCase(fetchCategoriesByMainCategory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Add Category
            .addCase(addCategory.fulfilled, (state, action: PayloadAction<Category>) => {
                state.categories.push(action.payload);
                state.categories.sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999));
            })
            // Update Category
            .addCase(updateCategory.fulfilled, (state, action: PayloadAction<Category>) => {
                const index = state.categories.findIndex(c => c._id === action.payload._id);
                if (index !== -1) {
                    state.categories[index] = action.payload;
                }
                state.categories.sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999));
            })
            // Delete Category
            .addCase(deleteCategory.fulfilled, (state, action: PayloadAction<string>) => {
                state.categories = state.categories.filter(c => c._id !== action.payload);
            });
    },
});

export default categorySlice.reducer;
